import { DateRange } from '@/dashboards/ModelContributions/components/ActionBar/DateRange';
import { Filter } from '@/dashboards/ModelContributions/components/ActionBar/Filter';
import { FilterNode } from '@/dashboards/SpendSummary/SpendSummary';
import { SpendSummarySearch } from '@/dashboards/SpendSummary';
import { TreeNodeLike } from '@/components/TreeSelect/useTreeSelect/types';
import { ValueToggle } from '@analytical-alley/ui';
import { Label } from 'flowbite-react';

interface FilterControlsProps {
    search: SpendSummarySearch;
    nodes: TreeNodeLike[];
    minDate: Date;
    maxDate: Date;
    canReset: boolean;
    vertical?: boolean;
    onSetSearch: (search: Partial<SpendSummarySearch>) => void;
    onSetSelectedNodes: (nodes: FilterNode[]) => void;
    onResetClick: () => void;
}

const ModelBreakdown = ({
    value,
    onChange,
}: {
    value: 'category' | 'variable';
    onChange: (value: 'category' | 'variable') => void;
}) => (
    <div className="flex flex-col items-start h-full">
        <Label className="font-mono font-normal mb-1" htmlFor="modelBreakdown">
            Model breakdown
        </Label>
        <ValueToggle
            compact={false}
            options={[
                {
                    label: 'Category',
                    value: 'category',
                    tooltip: 'Broader, more high-level view',
                },
                {
                    label: 'Variable',
                    value: 'variable',
                    tooltip: 'Detailed, more granular view',
                },
            ]}
            value={value}
            onChange={onChange}
        />
    </div>
);

const ResetButton = ({ onClick }: { onClick: () => void }) => (
    <button
        className="glass-tile self-end p-1.5 rounded-md shadow-lg hover:bg-white/30"
        onClick={onClick}
    >
        Reset
    </button>
);

export const FilterControls = ({
    search,
    nodes,
    minDate,
    maxDate,
    canReset,
    vertical = false,
    onSetSearch,
    onSetSelectedNodes,
    onResetClick,
}: FilterControlsProps) => {
    const dateRange = (
        <DateRange
            minDate={minDate}
            maxDate={maxDate}
            searchStartDate={search.startDate}
            searchEndDate={search.endDate}
            onSetSearch={onSetSearch}
        />
    );

    const modelBreakdown = (
        <ModelBreakdown
            value={search.modelBreakdown}
            onChange={(value) => onSetSearch({ modelBreakdown: value })}
        />
    );

    const filter = (
        <Filter
            nodes={nodes}
            onSetSelectedNodes={onSetSelectedNodes}
            searchFilter={search.filter}
            onSetSearch={onSetSearch}
        />
    );

    if (vertical) {
        return (
            <div className="flex flex-col gap-6 pt-2">
                {modelBreakdown}
                {dateRange}
                {filter}
                {canReset && <ResetButton onClick={onResetClick} />}
            </div>
        );
    }

    return (
        <>
            <div className="flex gap-4 items-end">
                {canReset && <ResetButton onClick={onResetClick} />}
                {dateRange}
            </div>
            <div className="flex gap-4 items-end">
                {modelBreakdown}
                {filter}
            </div>
        </>
    );
};
